import { createRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@arabquiz/ui'
import { Route as rootRoute } from '../__root'
import { mapServerMessageToKey } from './auth-errors'

/**
 * Sign-up route — Arabic, RTL, validation feedback.
 *
 * Mirrors `sign-in.tsx` but posts to `/api/v1/auth/sign-up`. On success the
 * API auto-logs-in the new user (200 + a MeResponse session payload and a
 * Set-Cookie), so we invalidate the cached `/me` query and navigate straight
 * to the authenticated dashboard — exactly like `sign-in.tsx`.
 *
 * Validation enforced client-side:
 *  - displayName: required
 *  - email: required + RFC-ish regex
 *  - password: required + min 10 chars (matches API password policy)
 *  - confirmPassword: required + must equal `password` (re-validated when
 *    `password` changes via `onChangeListenTo`).
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/sign-up',
  component: SignUpPage,
})

type SignUpPayload = {
  displayName: string
  email: string
  password: string
  confirmPassword: string
}

type ValidationKey =
  | 'required'
  | 'invalidEmail'
  | 'passwordTooShort'
  | 'passwordMismatch'
  | 'passwordRequiresUpper'
  | 'passwordRequiresLower'
  | 'passwordRequiresDigit'
  | 'passwordRequiresSymbol'
  | 'emailOrPasswordInvalid'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MIN_PASSWORD_LENGTH = 8

/**
 * Minimal identity shape this page reads from the shared `['auth', 'me']`
 * cache. We only need a truthy/null signal to decide the redirect, so the
 * exact field set is irrelevant — mirrors `_app.tsx`'s `MeResponse`.
 */
type MeResponse = {
  userId: string
  email: string
  displayName: string
  emailConfirmed: boolean
  organizationId: string
  locale: string
}

/**
 * Structured error thrown by the sign-up mutation. Carries either the
 * RFC7807 ProblemDetails field-errors map (FluentValidation) or the single
 * business `error` code, so the UI can render localized Arabic messages
 * instead of leaking raw English server text.
 */
class SignUpError extends Error {
  readonly fieldErrors?: Record<string, string[]> | undefined
  readonly errorCode?: string | undefined

  constructor(detail: { fieldErrors?: Record<string, string[]>; errorCode?: string }) {
    super('sign-up-failed')
    this.name = 'SignUpError'
    this.fieldErrors = detail.fieldErrors
    this.errorCode = detail.errorCode
  }
}

function SignUpPage() {
  const { t } = useTranslation('auth')
  const { t: tValidation } = useTranslation('validation')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Reuse the shared `['auth', 'me']` cache (same key/shape as `_app.tsx`):
  // an already-signed-in visitor who lands on the register page is bounced
  // to the app. 401/non-ok resolves to `null` (anonymous) rather than
  // throwing — this page is fine for guests.
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/auth/me', {
        credentials: 'include',
        signal,
      })
      if (res.status === 401) return null
      if (!res.ok) return null
      return (await res.json()) as MeResponse
    },
    retry: false,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (meQuery.data) {
      void navigate({ to: '/dashboard' })
    }
  }, [meQuery.data, navigate])

  const signUpMutation = useMutation({
    mutationFn: async (payload: Omit<SignUpPayload, 'confirmPassword'>) => {
      const res = await fetch('/api/v1/auth/sign-up', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })
      if (!res.ok) {
        // Two possible failure shapes:
        //  1. RFC7807 ProblemDetails -> { errors: { Field: ["..."] } }
        //  2. Business failure        -> { error: "EmailOrPasswordInvalid" }
        let body: unknown = null
        try {
          body = await res.json()
        } catch {
          // Non-JSON body (unexpected); fall through to the generic error.
        }

        if (body && typeof body === 'object') {
          const problem = body as {
            errors?: Record<string, string[]>
            error?: string
          }
          if (problem.errors && typeof problem.errors === 'object') {
            throw new SignUpError({ fieldErrors: problem.errors })
          }
          if (typeof problem.error === 'string') {
            throw new SignUpError({ errorCode: problem.error })
          }
        }

        throw new SignUpError({})
      }
      // Sign-up auto-logs-in: the response is the same session/me payload as
      // sign-in (and carries the auth cookie via Set-Cookie).
      return res.json() as Promise<{
        id: string
        email: string
        displayName: string
        emailConfirmed: boolean
        organizationId: string
        locale: string
      }>
    },
    onSuccess: async () => {
      // Drop any stale (e.g. unauthorized) `/me` result so the `_app` auth
      // gate refetches the now-authenticated session instead of reusing a
      // cached signed-out state.
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      // Land in the authenticated app (gated by `_app`) — the new user is
      // already signed in via the cookie set by the sign-up response.
      void navigate({ to: '/dashboard' })
    },
  })

  const form = useForm({
    defaultValues: {
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
    } as SignUpPayload,
    onSubmit: async ({ value }) => {
      const { confirmPassword: _confirmPassword, ...payload } = value
      await signUpMutation.mutateAsync(payload)
    },
  })

  // Translate the structured server error into a de-duplicated list of Arabic
  // messages. Unknown server strings fall back to the generic error so the box
  // never renders raw English or breaks.
  const errorMessages: string[] = (() => {
    const error = signUpMutation.error
    if (!(error instanceof SignUpError)) return [tCommon('error')]

    if (error.errorCode === 'EmailOrPasswordInvalid') {
      return [tValidation('emailOrPasswordInvalid')]
    }

    if (error.fieldErrors) {
      const messages = Object.values(error.fieldErrors)
        .flat()
        .map((serverMessage) => {
          const key = mapServerMessageToKey(serverMessage)
          return key ? tValidation(key) : tCommon('error')
        })
      const unique = [...new Set(messages)]
      if (unique.length > 0) return unique
    }

    return [tCommon('error')]
  })()

  // While identity is resolving, or once we know the visitor is signed in
  // (redirect scheduled in the effect above), render nothing so we never
  // flash the register form to an authenticated user.
  if (meQuery.isLoading || meQuery.data) {
    return null
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10">
          <div className="mb-8 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold font-display text-brand-900 tracking-tight">
              {t('signUp')}
            </h1>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            noValidate
          >
            <div className="space-y-5">
              <form.Field
                name="displayName"
                validators={{
                  onChange: ({ value }) => (!value ? 'required' : undefined),
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('displayName')}
                      </label>
                      <Input
                        id={field.name}
                        type="text"
                        name={field.name}
                        autoComplete="name"
                        dir="auto"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              <form.Field
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return 'required'
                    if (!EMAIL_PATTERN.test(value)) return 'invalidEmail'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('email')}
                      </label>
                      <Input
                        id={field.name}
                        type="email"
                        name={field.name}
                        autoComplete="email"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              <form.Field
                name="password"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return 'required'
                    if (value.length < MIN_PASSWORD_LENGTH) return 'passwordTooShort'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('password')}
                      </label>
                      <Input
                        id={field.name}
                        type="password"
                        name={field.name}
                        autoComplete="new-password"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              <form.Field
                name="confirmPassword"
                validators={{
                  onChangeListenTo: ['password'],
                  onChange: ({ value, fieldApi }) => {
                    if (!value) return 'required'
                    const passwordValue = fieldApi.form.getFieldValue('password')
                    if (value !== passwordValue) return 'passwordMismatch'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('confirmPassword')}
                      </label>
                      <Input
                        id={field.name}
                        type="password"
                        name={field.name}
                        autoComplete="new-password"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              {signUpMutation.isError ? (
                <div
                  className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2"
                  role="alert"
                >
                  <ul className="list-disc space-y-1 ps-5">
                    {errorMessages.map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={signUpMutation.isPending}
                className="w-full"
              >
                {signUpMutation.isPending ? tCommon('loading') : t('signUp')}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-brand-800">
            <span>{t('haveAccount')} </span>
            <a
              href="/auth/sign-in"
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('signIn')}
            </a>
          </p>
        </div>
      </div>
    </main>
  )
}
